/* Scoped PDP adapter. Cart transport and gift relationships remain owned by the theme.
   Presentation: a stacked card deck is the only visible selector; gifts open in a dialog
   that grows out of the tapped gift control. One commerce state: state.choice. */
(() => {
  'use strict';
  /* Customer-facing strings come from Shopify locale keys through the shared
     Liquid → JS bridge (snippets/fw-landing-i18n.liquid). The second argument is
     the English fallback, so a page that does not render the bridge behaves
     exactly as before. %tokens% are substituted here, never in the locale file. */
  const i18n = (key, fallback, vars) => {
    let value = (window.FWI18n && window.FWI18n.t(key, fallback)) || fallback;
    if (vars) Object.keys(vars).forEach((name) => { value = value.split(`%${name}%`).join(String(vars[name])); });
    return value;
  };
  const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
  // Resting tilt of each card shell when it is not the active card (content counter-rotates).
  const SHELL_TILT = [-4, -2, 1, 3];
  // Deck geometry per |offset| from the active card: x as a fraction of card width, scale.
  const DECK = {
    mobile: { x: [0, 0.075, 0.095, 0.11], scale: [1, 0.965, 0.925, 0.9] },
    desktop: { x: [0, 0.5, 0.85, 1.1], scale: [1, 0.92, 0.85, 0.8] },
  };

  function init(root) {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    const $ = (selector) => root.querySelector(selector);
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const wide = window.matchMedia('(min-width: 990px)');
    const choices = Array.from(root.querySelectorAll('[data-choice]')).map((card) => ({ card, ...JSON.parse(card.dataset.choice) }));
    const addons = Array.from(root.querySelectorAll('[data-addon]')).map((element) => ({
      element, id: element.dataset.addon, family: element.dataset.family,
      kind: element.dataset.kind, defaultVariant: element.dataset.defaultVariant,
    }));
    const form = $('.rfp-form');
    if (!form || !choices.length) return;
    const quantityInput = $('#rfp-quantity');
    const atcButtons = Array.from(root.querySelectorAll('[data-add-to-cart]'));
    const state = { choice: null, quantity: 1, addons: new Map(), busy: false };
    const eligible = (addon) => addon.family === 'all' || addon.family === state.choice?.family;
    const fitsFamily = (addon, family) => addon.family === 'all' || addon.family === family;

    function resetAddons() {
      state.addons.clear();
      addons.forEach((addon) => {
        state.addons.set(addon.id, { variantId: addon.defaultVariant, selected: eligible(addon) && addon.kind === 'gift' });
        const controls = addon.element.querySelectorAll('[data-addon-variant]');
        controls.forEach((control) => {
          if (control.matches('select')) control.value = addon.defaultVariant;
          if (control.matches('[type="radio"]')) control.checked = control.value === addon.defaultVariant;
        });
      });
    }
    function syncAddons() {
      const ids = [];
      addons.forEach((addon) => {
        const selected = state.addons.get(addon.id);
        const visible = eligible(addon);
        addon.element.hidden = !visible;
        const toggle = addon.element.querySelector('[data-addon-toggle]');
        toggle.checked = visible && selected.selected;
        toggle.disabled = !visible || state.busy;
        addon.element.classList.toggle('is-active', toggle.checked);
        const variantControls = Array.from(addon.element.querySelectorAll('[data-addon-variant]'));
        if (variantControls.length) {
          variantControls.forEach((control) => { control.disabled = !visible || state.busy || control.dataset.available === 'false'; });
          const option = variantControls.find((control) => control.matches('select'))?.selectedOptions[0]
            || variantControls.find((control) => control.matches('[type="radio"]:checked'));
          if (option) {
            const image = addon.element.querySelector('img');
            if (image) { image.src = option.dataset.image; image.removeAttribute('srcset'); }
            const price = addon.element.querySelector('[data-addon-price]');
            const compare = addon.element.querySelector('[data-addon-compare]');
            if (price) price.innerHTML = option.dataset.price;
            if (compare) { compare.innerHTML = option.dataset.compare; compare.hidden = !option.dataset.compare; }
          }
        }
        if (toggle.checked) ids.push(selected.variantId);
      });
      root.querySelectorAll('[data-addon-group]').forEach((group) => {
        group.hidden = !Array.from(group.querySelectorAll('[data-addon]')).some((item) => !item.hidden);
      });
      // Compatibility output only; never a second source of commerce state.
      $('#fwo-free-gifts-data').textContent = JSON.stringify({ variant_ids: ids });
    }
    function syncButtons() {
      atcButtons.forEach((button) => {
        const choice = choices.find((item) => item.card.contains(button));
        const available = Boolean(choice ? choice.available : state.choice?.available);
        const isActive = !choice || choice === state.choice;
        button.disabled = state.busy || !available;
        button.setAttribute('aria-busy', String(state.busy && isActive));
        const label = button.querySelector('[data-atc-label]') || button;
        label.textContent =
          state.busy && isActive
            ? i18n('cart.adding', 'Adding…')
            : available
              ? i18n('cart.add', 'Add to cart')
              : i18n('cart.sold_out', 'Sold out');
      });
      const buyNow = $('[data-buy-now]');
      if (buyNow) {
        buyNow.disabled = state.busy || !state.choice?.available;
        buyNow.setAttribute('aria-busy', String(state.busy));
      }
      quantityInput.disabled = state.busy;
      root.classList.toggle('is-busy', state.busy);
    }
    function setQuantity(value) {
      const min = state.choice?.min || 1;
      const step = state.choice?.step || 1;
      const max = state.choice?.max;
      let next = Math.max(min, Number.isFinite(value) ? value : min);
      if (max != null) next = Math.min(next, max);
      state.quantity = min + Math.floor((next - min) / step) * step;
      quantityInput.value = state.quantity;
      syncButtons();
    }

    /* ── Deck: layout from the canonical choice, drag, keyboard, dots ── */
    const deck = $('[data-deck]');
    const track = $('[data-deck-track]');
    const dots = Array.from(root.querySelectorAll('[data-deck-dot]'));
    const activeIndex = () => Math.max(0, choices.indexOf(state.choice));
    const lookup = (list, value) => {
      const a = Math.min(value, list.length - 1);
      const low = Math.floor(a);
      const high = Math.min(low + 1, list.length - 1);
      return list[low] + (list[high] - list[low]) * (a - low);
    };
    function layout(dragX = 0) {
      const geometry = wide.matches ? DECK.desktop : DECK.mobile;
      const current = activeIndex();
      const width = choices[current].card.offsetWidth || 1;
      const progress = dragX / width;
      choices.forEach((choice, index) => {
        const isCurrent = index === current;
        const offset = isCurrent ? 0 : index - current + progress;
        const distance = Math.abs(offset);
        const direction = Math.sign(offset);
        const x = isCurrent ? dragX : direction * lookup(geometry.x, distance) * width;
        const tilt = SHELL_TILT[index % SHELL_TILT.length];
        const rotation = isCurrent ? Math.max(-5, Math.min(5, progress * 5)) : tilt * Math.min(distance, 1);
        const scale = isCurrent ? 1 : lookup(geometry.scale, distance);
        choice.card.style.transform = `translate3d(${x.toFixed(2)}px, 0, 0) rotate(${rotation.toFixed(3)}deg) scale(${scale.toFixed(4)})`;
        choice.card.style.setProperty('--rfp-counter', `${(-rotation).toFixed(3)}deg`);
        choice.card.style.zIndex = String(isCurrent ? 50 : 40 - Math.round(distance * 10));
        choice.card.style.opacity = distance > 2 ? String(Math.max(0, 3 - distance)) : '1';
        choice.card.classList.toggle('is-active', isCurrent);
      });
    }
    function syncDeck() {
      const current = activeIndex();
      choices.forEach((choice, index) => {
        const active = index === current;
        choice.card.setAttribute('aria-hidden', String(!active));
        const content = choice.card.querySelector('.rfp-card-content');
        if (content) content.inert = !active;
      });
      dots.forEach((dot, index) => {
        dot.setAttribute('aria-current', String(index === current));
        dot.disabled = state.busy;
      });
      layout();
    }
    // Cards know up front whether their family has gifts (existing eligibility rule, per card).
    choices.forEach((choice) => {
      const free = addons.some((addon) => addon.kind === 'gift' && fitsFamily(addon, choice.family));
      const any = addons.some((addon) => fitsFamily(addon, choice.family));
      const badge = choice.card.querySelector('[data-card-gifts-badge]');
      const trigger = choice.card.querySelector('[data-gift-trigger]');
      if (badge) badge.hidden = !free;
      if (trigger) trigger.hidden = !any;
    });
    dots.forEach((dot, index) => {
      const choice = choices[index];
      if (choice) {
        dot.setAttribute(
          'aria-label',
          i18n('a11y.show_choice', 'Show %name% (%index% of %total%)', {
            name: choice.name || choice.label,
            index: index + 1,
            total: choices.length,
          })
        );
      }
    });

    function selectChoice(choice) {
      if (state.busy || !choice?.id) return;
      const changedFamily = state.choice?.family !== choice.family;
      state.choice = choice;
      root.dataset.activeStrap = choice.family;
      root.dataset.productId = choice.productId;
      root.dataset.variantId = choice.id;
      $('[data-price]').innerHTML = choice.priceHtml;
      $('[data-compare-price]').innerHTML = choice.compareHtml;
      $('[data-compare-price]').hidden = !(choice.compare > choice.price);
      $('[data-selection-status]').textContent = choice.available
        ? i18n('a11y.choice_selected', '%name% selected.', { name: choice.name || choice.label })
        : i18n('a11y.choice_selected_sold_out', '%name% selected. Sold out.', { name: choice.name || choice.label });
      $('[data-selected-variant]').value = choice.id;
      const productIdInput = form.querySelector('[name="product-id"]');
      if (productIdInput) productIdInput.value = choice.productId;
      form.dataset.productId = choice.productId;
      if (changedFamily) resetAddons();
      syncAddons();
      setQuantity(state.quantity);
      syncDeck();
      $('[data-error]').hidden = true;
      root.dispatchEvent(new CustomEvent('roulette:selection-change', {
        bubbles: true, detail: { family: choice.family, productId: choice.productId, variantId: choice.id },
      }));
    }
    const selectIndex = (index) => {
      const choice = choices[Math.max(0, Math.min(index, choices.length - 1))];
      if (choice !== state.choice) selectChoice(choice);
      else layout();
    };

    function cartItems() {
      const items = [{ id: state.choice.id, quantity: state.quantity }];
      const seen = new Set([String(state.choice.id)]);
      addons.forEach((addon) => {
        const selection = state.addons.get(addon.id);
        if (!eligible(addon) || !selection.selected || seen.has(selection.variantId)) return;
        seen.add(selection.variantId);
        // Existing PDP quantities: one selected gift / upsell per configuration.
        items.push({ id: Number(selection.variantId), quantity: 1, properties: { _addon_kind: addon.kind === 'gift' ? 'free_gift' : 'paid_addon' } });
      });
      return items;
    }
    async function purchase(checkout, trigger) {
      if (state.busy || !state.choice?.available) return;
      setQuantity(Number(quantityInput.value));
      state.busy = true;
      syncButtons();
      syncAddons();
      syncDeck();
      const error = $('[data-error]');
      error.hidden = true;
      try {
        if (!window.FairwhaleLandingCart) throw new Error(i18n('errors.cart_not_ready', 'The cart is not ready. Please try again.'));
        // Shared transport owns errors, native parent relationships, drawer and cart:updated.
        await window.FairwhaleLandingCart.addItems(cartItems(), trigger);
        if (checkout) {
          const checkoutForm = $('[data-checkout-form]');
          checkoutForm.requestSubmit(checkoutForm.querySelector('[name="checkout"]'));
        }
      } catch (exception) {
        error.textContent = exception.message || i18n('errors.cart_add_failed', 'Unable to add your selection. Please try again.');
        error.hidden = false;
        // Show the error on the card whose button was used.
        const card = trigger?.closest?.('.rfp-card-content');
        if (card && error.parentElement !== card) card.append(error);
      } finally {
        state.busy = false;
        syncButtons();
        syncAddons();
        syncDeck();
      }
    }

    // Swipe: horizontal drags move the top card; vertical page scrolling stays native (touch-action: pan-y).
    let drag = null;
    let suppressClick = false;
    track.addEventListener('pointerdown', (event) => {
      if (state.busy || (event.pointerType === 'mouse' && event.button !== 0)) return;
      drag = { id: event.pointerId, x: event.clientX, y: event.clientY, t: event.timeStamp, dx: 0, active: false };
    });
    track.addEventListener('pointermove', (event) => {
      if (!drag || event.pointerId !== drag.id) return;
      const dx = event.clientX - drag.x;
      const dy = event.clientY - drag.y;
      if (!drag.active) {
        if (Math.abs(dy) > 10 && Math.abs(dy) > Math.abs(dx)) { drag = null; return; }
        if (Math.abs(dx) < 8 || Math.abs(dx) < Math.abs(dy) * 1.2) return;
        drag.active = true;
        track.setPointerCapture?.(event.pointerId);
        deck.classList.add('is-dragging');
      }
      const current = activeIndex();
      const atEdge = (dx > 0 && current === 0) || (dx < 0 && current === choices.length - 1);
      drag.dx = atEdge ? dx * 0.3 : dx;
      layout(drag.dx);
    });
    const endDrag = (event) => {
      if (!drag || event.pointerId !== drag.id) return;
      const finished = drag;
      drag = null;
      if (!finished.active) return;
      deck.classList.remove('is-dragging');
      suppressClick = true;
      window.setTimeout(() => { suppressClick = false; }, 0);
      const width = choices[activeIndex()].card.offsetWidth || 1;
      const velocity = finished.dx / Math.max(1, event.timeStamp - finished.t);
      // Far enough, or a real flick (fast and more than a nudge).
      const threshold = Math.abs(finished.dx) > width * 0.18 || (Math.abs(finished.dx) > 40 && Math.abs(velocity) > 0.45);
      if (threshold && finished.dx < 0) selectIndex(activeIndex() + 1);
      else if (threshold && finished.dx > 0) selectIndex(activeIndex() - 1);
      else layout();
    };
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener('click', (event) => {
      if (suppressClick) { event.preventDefault(); event.stopPropagation(); return; }
      // A peeking card is picked by tapping it (its content is inert until it becomes active).
      const card = event.target.closest('.rfp-card');
      const choice = card && choices.find((item) => item.card === card);
      if (choice && choice !== state.choice) { event.preventDefault(); selectChoice(choice); }
    }, true);
    deck.addEventListener('keydown', (event) => {
      if (event.target !== deck) return;
      if (event.key === 'ArrowRight') { event.preventDefault(); selectIndex(activeIndex() + 1); }
      if (event.key === 'ArrowLeft') { event.preventDefault(); selectIndex(activeIndex() - 1); }
      if (event.key === 'Home') { event.preventDefault(); selectIndex(0); }
      if (event.key === 'End') { event.preventDefault(); selectIndex(choices.length - 1); }
    });
    dots.forEach((dot, index) => dot.addEventListener('click', () => selectIndex(index)));
    let resizeFrame = 0;
    const relayout = () => { cancelAnimationFrame(resizeFrame); resizeFrame = requestAnimationFrame(() => layout()); };
    window.addEventListener('resize', relayout, { passive: true });
    wide.addEventListener?.('change', relayout);

    /* ── Gift dialog: FLIP from the tapped gift control, focus trap, scroll lock ── */
    const modal = $('[data-gifts-modal]');
    const panel = $('[data-gifts-panel]');
    const backdrop = modal?.querySelector('.rfp-gifts-backdrop');
    let modalState = 'closed';
    let giftTrigger = null;
    let panelAnimation = null;
    let backdropAnimation = null;
    let scrollLock = null;

    function lockScroll() {
      const html = document.documentElement;
      scrollLock = { html: html.style.overflow, body: document.body.style.overflow, padding: document.body.style.paddingRight };
      const gutter = window.innerWidth - html.clientWidth;
      html.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      if (gutter > 0) document.body.style.paddingRight = `${gutter}px`;
    }
    function unlockScroll() {
      if (!scrollLock) return;
      document.documentElement.style.overflow = scrollLock.html;
      document.body.style.overflow = scrollLock.body;
      document.body.style.paddingRight = scrollLock.padding;
      scrollLock = null;
    }
    function originTransform() {
      const panelRect = panel.getBoundingClientRect();
      const source = giftTrigger?.isConnected ? giftTrigger.getBoundingClientRect() : null;
      if (!source || !panelRect.width) return 'translate(0, 24px) scale(0.9)';
      const dx = (source.left + source.width / 2) - (panelRect.left + panelRect.width / 2);
      const dy = (source.top + source.height / 2) - (panelRect.top + panelRect.height / 2);
      const scale = Math.max(0.08, Math.min(0.3, source.width / panelRect.width));
      return `translate(${dx.toFixed(1)}px, ${dy.toFixed(1)}px) scale(${scale.toFixed(3)})`;
    }
    const focusables = () => Array.from(panel.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'))
      .filter((element) => !element.disabled && !element.closest('[hidden]') && element.getClientRects().length);
    function setModalState(value) {
      modalState = value;
      root.dataset.giftsState = value;
    }
    function openGifts(trigger) {
      if (!modal || modalState === 'open' || modalState === 'opening') return;
      panelAnimation?.cancel();
      backdropAnimation?.cancel();
      giftTrigger = trigger;
      const subtitle = $('[data-gifts-subtitle]');
      if (subtitle) {
        const fallbackName = i18n('gifts.fallback_name', 'your Roulette');
        subtitle.textContent = i18n('gifts.with_selection', 'With %name%', {
          name: state.choice?.name || state.choice?.label || fallbackName,
        });
      }
      root.querySelectorAll('[data-addon]:not([hidden])').forEach((row, index) => row.style.setProperty('--rfp-row', index));
      if (modalState === 'closed') lockScroll();
      modal.hidden = false;
      modal.classList.add('is-open');
      trigger?.setAttribute('aria-expanded', 'true');
      setModalState('opening');
      const closeButton = panel.querySelector('.rfp-gifts-close');
      (closeButton || panel).focus({ preventScroll: true });
      if (motion.matches || !panel.animate) { setModalState('open'); return; }
      backdropAnimation = backdrop?.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 380, easing: 'ease-out' });
      panelAnimation = panel.animate([
        { transform: originTransform(), opacity: 0 },
        { opacity: 1, offset: 0.35 },
        { transform: 'none', opacity: 1 },
      ], { duration: 580, easing: EASE });
      panelAnimation.finished.then(() => { if (modalState === 'opening') setModalState('open'); }).catch(() => {});
    }
    function finishClose() {
      panelAnimation?.cancel();
      backdropAnimation?.cancel();
      panelAnimation = null;
      backdropAnimation = null;
      modal.hidden = true;
      modal.classList.remove('is-open');
      unlockScroll();
      giftTrigger?.setAttribute('aria-expanded', 'false');
      if (giftTrigger?.isConnected) giftTrigger.focus({ preventScroll: true });
      setModalState('closed');
    }
    function closeGifts() {
      if (!modal || modalState === 'closed' || modalState === 'closing') return;
      setModalState('closing');
      if (motion.matches || !panel.animate) { finishClose(); return; }
      panelAnimation?.cancel();
      backdropAnimation?.cancel();
      backdropAnimation = backdrop?.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 380, easing: 'ease-in', fill: 'forwards' });
      panelAnimation = panel.animate([
        { transform: 'none', opacity: 1 },
        { opacity: 1, offset: 0.55 },
        { transform: originTransform(), opacity: 0 },
      ], { duration: 460, easing: EASE, fill: 'forwards' });
      panelAnimation.finished.then(() => { if (modalState === 'closing') finishClose(); }).catch(() => {});
    }
    root.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-gift-trigger]');
      if (trigger && !trigger.disabled) { openGifts(trigger); return; }
      if (event.target.closest('[data-gifts-close]')) { event.preventDefault(); closeGifts(); }
    });
    document.addEventListener('keydown', (event) => {
      if (modalState !== 'open' && modalState !== 'opening') return;
      if (event.key === 'Escape') { event.preventDefault(); closeGifts(); return; }
      if (event.key !== 'Tab') return;
      const items = focusables();
      if (!items.length) { event.preventDefault(); panel.focus(); return; }
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      else if (!panel.contains(document.activeElement)) { event.preventDefault(); first.focus(); }
    });

    root.addEventListener('change', (event) => {
      if (event.target === quantityInput) setQuantity(Number(quantityInput.value));
      const element = event.target.closest('[data-addon]');
      if (!element || state.busy) return;
      const selection = state.addons.get(element.dataset.addon);
      if (event.target.matches('[data-addon-toggle]')) selection.selected = event.target.checked;
      if (event.target.matches('[data-addon-variant]')) selection.variantId = event.target.value;
      syncAddons();
    });
    root.addEventListener('click', (event) => {
      const button = event.target.closest('[data-buy-now]');
      if (button && !button.disabled) purchase(true, button);
    });
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const trigger = event.submitter || state.choice?.card.querySelector('[data-add-to-cart]') || atcButtons[0];
      purchase(false, trigger);
    });

    /* ── Entrance: heading → subheading → deck (transform + opacity only) ── */
    if ('IntersectionObserver' in window && !motion.matches) {
      root.classList.add('is-ready');
      const entrance = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        root.classList.add('is-visible');
        entrance.disconnect();
      }, { rootMargin: '0px 0px -12% 0px', threshold: 0.05 });
      entrance.observe(root);
    }

    const initialVariant = new URL(window.location.href).searchParams.get('variant');
    selectChoice(choices.find((choice) => String(choice.id) === initialVariant)
      || choices.find((choice) => choice.id && choice.available) || choices.find((choice) => choice.id));
    // Commit the first layout without transitions, then enable deck motion.
    track.getBoundingClientRect();
    requestAnimationFrame(() => requestAnimationFrame(() => root.classList.add('is-deck-ready')));
  }
  const start = () => document.querySelectorAll('[data-roulette-purchase]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
